import { apiClient } from './client'

export interface RouteStop {
  stopId: string
  name: string
  address: string
  latitude: number
  longitude: number
  estimatedTime: string // HH:MM format
  students: string[] // Student IDs
  order: number
}

export interface RouteSchedule {
  departure: {
    time: string // HH:MM format
    location: string
  }
  arrival: {
    time: string // HH:MM format  
    location: string
  }
}

export interface TransportRoute {
  id: string
  institutionId: string
  routeName: string
  routeNumber: string
  description?: string
  assignedVehicleIds: string[]
  assignedDriverIds: string[]
  stops: RouteStop[]
  schedule: RouteSchedule
  operatingDays: ('monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday')[]
  isActive: boolean
  createdById: string
  createdAt: string
  updatedAt: string
  // Enhanced fields
  assignedVehicles?: {
    id: string
    vehicleNumber: string
    make: string
    vehicleModel: string
    year?: number
    capacity?: number
    status: string
    currentLocation?: any
  }[]
  assignedDrivers?: {
    id: string
    firstName: string
    lastName: string
    email: string
    phone: string
  }[]
  recentTrips?: {
    id: string
    tripDate: string
    tripType: string
    status: string
    vehicle?: any
    driver?: any
  }[]
  stopCount?: number
  operatingDaysCount?: number
}

export interface CreateRouteRequest {
  routeName: string
  routeNumber: string
  description?: string
  assignedVehicleIds?: string[]
  assignedDriverIds?: string[]
  stops: Omit<RouteStop, 'stopId' | 'order'>[]
  schedule: RouteSchedule
  operatingDays: ('monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday')[]
  institutionId?: string // For super admins
}

export interface UpdateRouteRequest {
  routeName?: string
  routeNumber?: string
  description?: string
  assignedVehicleIds?: string[]
  assignedDriverIds?: string[]
  stops?: RouteStop[]
  schedule?: RouteSchedule
  operatingDays?: ('monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday')[]
}

export interface GetRoutesParams {
  page?: number
  limit?: number
  search?: string
  status?: string
  assignedDriverId?: string
  assignedVehicleId?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  institutionId?: string // For super admins
}

export interface RouteStats {
  totalRoutes: number
  activeRoutes: number
  routesWithVehicles: number
  routesWithDrivers: number
  routesWithoutVehicles: number
  routesWithoutDrivers: number
  totalStops: number
  averageStopsPerRoute: number
}

// Get all routes with optional filtering and pagination
export const getRoutes = async (params: GetRoutesParams = {}) => {
  const searchParams = new URLSearchParams()
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value.toString())
    }
  })
  
  const response = await apiClient.get(`/routes?${searchParams.toString()}`)
  return response.data
}

// Get single route by ID
export const getRoute = async (id: string) => {
  const response = await apiClient.get(`/routes/${id}`)
  return response.data
}

// Create new route
export const createRoute = async (routeData: CreateRouteRequest) => {
  const response = await apiClient.post('/routes', routeData)
  return response.data
}

// Update existing route
export const updateRoute = async (id: string, routeData: UpdateRouteRequest) => {
  const response = await apiClient.put(`/routes/${id}`, routeData)
  return response.data
}

// Delete route (soft delete)
export const deleteRoute = async (id: string) => {
  const response = await apiClient.delete(`/routes/${id}`)
  return response.data
}

// Get route statistics
export const getRouteStats = async (institutionId?: string) => {
  const params = institutionId ? `?institutionId=${institutionId}` : ''
  const response = await apiClient.get(`/routes/stats${params}`)
  return response.data
}

// Helper functions for route management
export const addStopToRoute = async (routeId: string, stop: Omit<RouteStop, 'stopId' | 'order'>) => {
  const route = await getRoute(routeId)
  const newStops = [
    ...route.data.route.stops,
    {
      ...stop,
      stopId: `stop_${route.data.route.stops.length + 1}`,
      order: route.data.route.stops.length + 1
    }
  ]
  
  return updateRoute(routeId, { stops: newStops })
}

export const removeStopFromRoute = async (routeId: string, stopId: string) => {
  const route = await getRoute(routeId)
  const filteredStops = route.data.route.stops
    .filter((stop: RouteStop) => stop.stopId !== stopId)
    .map((stop: RouteStop, index: number) => ({
      ...stop,
      order: index + 1
    }))
  
  return updateRoute(routeId, { stops: filteredStops })
}

export const reorderRouteStops = async (routeId: string, stops: RouteStop[]) => {
  const orderedStops = stops.map((stop, index) => ({
    ...stop,
    order: index + 1
  }))
  
  return updateRoute(routeId, { stops: orderedStops })
}

export const assignVehicleToRoute = async (routeId: string, vehicleId: string) => {
  const route = await getRoute(routeId)
  const assignedVehicleIds = route.data.route.assignedVehicleIds || []
  
  if (!assignedVehicleIds.includes(vehicleId)) {
    assignedVehicleIds.push(vehicleId)
    return updateRoute(routeId, { assignedVehicleIds })
  }
  
  return route
}

export const assignDriverToRoute = async (routeId: string, driverId: string) => {
  const route = await getRoute(routeId)
  const assignedDriverIds = route.data.route.assignedDriverIds || []
  
  if (!assignedDriverIds.includes(driverId)) {
    assignedDriverIds.push(driverId)
    return updateRoute(routeId, { assignedDriverIds })
  }
  
  return route
}